/**
 * useBookingReviews — Fetch reservation reviews for BookingDetailModal
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 * Phase 5b-2a: Extracted from BookingDetailModal.jsx reviews effect/state.
 *
 * Purpose:
 *   Fetch owner-visible reviews for a reservation when the modal is open and reservation_id
 *   is set. Clears on reservation change; resets when modal closes. Same endpoint, params,
 *   gating, and loading/error behavior as the original inline effect.
 *
 * Workflow:
 *   1. When isOpen && reservationId: clear previous data/error if any, then fetch if not
 *      already loading and no data/error (same condition as original modal effect).
 *   2. Client-side 8s timeout; AbortController + isActive guard to avoid setState after
 *      unmount or dependency change. AbortError handled silently.
 *   3. When !isOpen: reset state.
 *
 * Assumptions & Invariants:
 *   - getReservationReviewsFn(reservationId) returns a Promise; same contract as ownerApi.getReservationReviews.
 *   - Effect deps [isOpen, reservationId] preserve original trigger behavior.
 *   - No new endpoints; no change to call count or timing.
 *
 * Dependencies:
 *   - React (useState, useEffect)
 *   - getReservationReviewsFn passed in by caller (ownerApi.getReservationReviews)
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 5b-2a: Extracted from BookingDetailModal.jsx; AbortController + isActive added.
 */

import { useState, useEffect } from 'react';

/**
 * @param {Object} opts
 * @param {boolean} opts.isOpen - Modal open
 * @param {string|number|null|undefined} opts.reservationId - Reservation ID (booking?.reservation_id || booking?.id)
 * @param {function(string|number): Promise<Object>} opts.getReservationReviewsFn - e.g. getReservationReviews from ownerApi
 * @returns {{ reviewsLoading: boolean, reviewsError: string|null, reviews: Array, refreshReviews: function }}
 */
export function useBookingReviews({ isOpen, reservationId, getReservationReviewsFn }) {
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);

  useEffect(() => {
    const abortController = new AbortController();
    let isActive = true;
    let timeoutId = null;

    if (isOpen && reservationId) {
      // Clear state when reservation_id changes (prevent stale content) — same as original
      if (reviews.length > 0 || reviewsError) {
        setReviews([]);
        setReviewsError(null);
      }

      // Fetch if not already loading and not already fetched — same condition as original
      if (!reviewsLoading && !reviews.length && !reviewsError) {
        const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
        const debugReviews = urlParams?.get('debug_reviews') === '1';

        if (debugReviews) {
          console.log('[REVIEWS_DEBUG] Fetch initiated:', {
            resId: reservationId,
            fetchUrl: `/api/v1/owner/reservations/${reservationId}/reviews`,
          });
        }

        if (isActive) {
          setReviewsLoading(true);
          setReviewsError(null);
        }

        timeoutId = setTimeout(() => {
          if (isActive) {
            console.warn('[BookingDetailModal] Reviews fetch timeout after 8s');
            setReviewsLoading(false);
            setReviewsError('Could not load reviews (timeout)');
            setReviews([]);
          }
        }, 8000);

        getReservationReviewsFn(reservationId)
          .then((data) => {
            clearTimeout(timeoutId);
            if (!isActive) return;

            if (debugReviews) {
              console.log('[REVIEWS_DEBUG] Fetch response:', {
                resId: reservationId,
                outcome: data?.outcome,
                status: 200,
                payloadKeys: Object.keys(data || {}),
                hasReviewsTopLevel: Array.isArray(data?.reviews),
                hasReviewsNested: Array.isArray(data?.data?.reviews),
                reviewsCount: (data?.reviews || data?.data?.reviews || []).length,
                reviewsArray: data?.reviews || data?.data?.reviews || null,
              });
            }

            if (data?.outcome === 'success') {
              const reviewsArray = data.reviews || data.data?.reviews || [];
              const filteredReviews = reviewsArray.filter((r) => r && r.status === 'published');
              setReviews(filteredReviews);
              setReviewsError(null);
            } else {
              setReviews([]);
              setReviewsError(data?.message || 'Failed to load reviews');
            }
          })
          .catch((err) => {
            clearTimeout(timeoutId);
            if (!isActive) return;
            if (err?.name === 'AbortError') return;

            if (debugReviews) {
              console.error('[REVIEWS_DEBUG] Fetch error:', {
                resId: reservationId,
                status: err?.status,
                statusText: err?.statusText,
                message: err?.message,
                errorCode: err?.errorCode,
                outcome: err?.outcome,
              });
            }
            console.error('[BookingDetailModal] Error fetching reviews:', err);

            // 404: not found / no access (backend aligns with detail). 403: treat like 404 here so
            // supplemental tab data never shows a harsh error when the modal already loaded the booking
            // (some layers still return 403 for forbidden resource scope).
            if (err?.status === 404 || err?.status === 403) {
              setReviews([]);
              setReviewsError(null);
            } else if (err?.status === 401 || err?.status === 500) {
              setReviews([]);
              setReviewsError(`Could not load reviews (HTTP ${err.status})`);
            } else {
              setReviews([]);
              setReviewsError(err?.message || `Could not load reviews (HTTP ${err?.status || 'unknown'})`);
            }
          })
          .finally(() => {
            clearTimeout(timeoutId);
            if (isActive) setReviewsLoading(false);
          });
      }
    } else if (!isOpen) {
      if (reviewsLoading || reviews.length > 0 || reviewsError) {
        setReviews([]);
        setReviewsLoading(false);
        setReviewsError(null);
      }
    }

    return () => {
      abortController.abort();
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOpen, reservationId]);

  // No manual refresh in modal today; return no-op for API parity
  const refreshReviews = () => {};

  return { reviewsLoading, reviewsError, reviews, refreshReviews };
}
